import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FloorTable, LayoutResponse, LayoutTable } from '../lib/api.js';
import { mirrorRotation, mirrorX, useDirection } from '../lib/direction.js';

/**
 * The floor plan.
 *
 * This is the component the whole RTL argument was about. CSS logical
 * properties flip text flow and padding; they do NOT flip absolutely positioned
 * pixel geometry. A room drawn at x=120 stays at x=120 when `dir` changes, so
 * an Arabic-speaking host would be looking at a mirror image of their own
 * restaurant — the terrace on the wrong side, the door where the kitchen is.
 *
 * So the geometry is mirrored explicitly here, at RENDER time only:
 *
 *   stored x  ──mirrorX──▶  rendered x        (never the other way into the DB)
 *
 * Coordinates are stored once, in left-to-right space. If a mirrored coordinate
 * ever reached the database, a venue that switched language once would corrupt
 * its own floor plan permanently.
 *
 * Table rotation mirrors too: a table angled 30° must lean the other way in a
 * mirrored room, or the plan stops matching the actual furniture.
 */

/** Table fill by status. High contrast, read across a room at a glance. */
const STATUS_FILL: Record<string, string> = {
  free: 'fill-emerald-100 stroke-emerald-500',
  held: 'fill-amber-100 stroke-amber-500',
  arrived: 'fill-sky-100 stroke-sky-500',
  seated: 'fill-blue-100 stroke-blue-600',
  dessert: 'fill-purple-100 stroke-purple-500',
  check_dropped: 'fill-orange-100 stroke-orange-500',
  bussing: 'fill-slate-200 stroke-slate-500',
};

interface Props {
  tables: FloorTable[];
  layout: LayoutResponse;
  locale: string;
  canMove: boolean;
  onDropBooking: (reservationId: string, tableId: string) => void;
  onSeat: (reservationId: string, version: number) => void;
}

export function FloorCanvas({ tables, layout, locale, canMove, onDropBooking }: Props) {
  const { t } = useTranslation();
  const dir = useDirection();
  const containerRef = useRef<HTMLDivElement>(null);
  const [dragOverTable, setDragOverTable] = useState<string | null>(null);

  const statusById = useMemo(
    () => new Map(tables.map((table) => [table.id, table])),
    [tables],
  );

  /** The drawing surface, sized to hold every table plus a margin. */
  const bounds = useMemo(() => {
    const width = Math.max(
      960,
      ...layout.tables.map((table) => table.x + table.width + 60),
    );
    const height = Math.max(
      520,
      ...layout.tables.map((table) => table.y + table.height + 60),
    );
    return { width, height };
  }, [layout.tables]);

  const roomsById = useMemo(
    () => new Map(layout.rooms.map((room) => [room.id, room])),
    [layout.rooms],
  );

  function render(table: LayoutTable) {
    const live = statusById.get(table.id);
    const status = live?.status ?? 'free';
    const reservation = live?.reservation ?? null;

    // The mirroring, in one line each. Everything else in this component is
    // ordinary layout.
    const x = mirrorX(table.x, table.width, bounds.width, dir);
    const rotation = mirrorRotation(table.rotation, dir);

    const room = roomsById.get(table.room_id);
    const roomName = room ? (locale === 'ar' ? room.name_ar : room.name_en) : '';

    return (
      <g
        key={table.id}
        transform={`translate(${x} ${table.y}) rotate(${rotation} ${table.width / 2} ${table.height / 2})`}
        onDragOver={(event) => {
          if (!canMove) return;
          event.preventDefault();
          setDragOverTable(table.id);
        }}
        onDragLeave={() => setDragOverTable((current) => (current === table.id ? null : current))}
        onDrop={(event) => {
          event.preventDefault();
          setDragOverTable(null);
          const reservationId = event.dataTransfer.getData('text/reservation-id');
          if (reservationId && canMove) onDropBooking(reservationId, table.id);
        }}
        role="group"
        aria-label={`${table.label} · ${roomName} · ${t(`floor.${status === 'check_dropped' ? 'checkDropped' : status}`)}`}
      >
        <rect
          width={table.width}
          height={table.height}
          rx={table.seat_type === 'bar' || table.seat_type === 'high_top' ? 6 : 14}
          className={[
            STATUS_FILL[status] ?? STATUS_FILL.free,
            'stroke-2 transition-colors',
            dragOverTable === table.id ? 'fill-brand-teal-light stroke-brand-teal' : '',
          ].join(' ')}
        />

        {/* Labels are counter-rotated so they stay readable however the table
            is angled, and are never mirrored - a mirrored "T12" is unreadable. */}
        <g transform={`rotate(${-rotation} ${table.width / 2} ${table.height / 2})`}>
          <text
            x={table.width / 2}
            y={reservation ? table.height / 2 - 8 : table.height / 2 + 4}
            textAnchor="middle"
            className="fill-slate-900 text-[13px] font-semibold"
            style={{ direction: 'ltr' }}
          >
            {table.label}
          </text>

          {reservation ? (
            <>
              <text
                x={table.width / 2}
                y={table.height / 2 + 8}
                textAnchor="middle"
                className="fill-slate-700 text-[11px]"
              >
                {reservation.guest_name ?? t('common.guest')}
              </text>
              <text
                x={table.width / 2}
                y={table.height / 2 + 22}
                textAnchor="middle"
                className="fill-slate-500 text-[10px]"
                style={{ direction: 'ltr' }}
              >
                {`${reservation.party_size}p`}
              </text>
            </>
          ) : (
            <text
              x={table.width / 2}
              y={table.height / 2 + 20}
              textAnchor="middle"
              className="fill-slate-400 text-[10px]"
              style={{ direction: 'ltr' }}
            >
              {`${table.min_party}-${table.max_party}`}
            </text>
          )}
        </g>

        {/* An allergy is the one guest fact that must be visible on the plan
            itself. A server crossing the room should not have to open a drawer
            to learn about a nut allergy. */}
        {reservation?.allergies ? (
          <circle cx={10} cy={10} r={5} className="fill-red-500" aria-hidden />
        ) : null}
        {reservation && reservation.vip_level > 0 ? (
          <circle
            cx={table.width - 10}
            cy={10}
            r={5}
            className="fill-brand-bronze"
            aria-hidden
          />
        ) : null}
      </g>
    );
  }

  return (
    <div
      ref={containerRef}
      className="overflow-auto rounded-lg border border-slate-200 bg-white p-2"
    >
      <svg
        viewBox={`0 0 ${bounds.width} ${bounds.height}`}
        width={bounds.width}
        height={bounds.height}
        className="max-w-full"
        role="img"
        aria-label={t('floor.title')}
      >
        {layout.tables.map(render)}
      </svg>
    </div>
  );
}
